import type { IteroBrowserTab } from './browserTabs';
import type { OrderTabId } from './order-view/orderTabIds';

const ITERO_PARAMS = {
    tab: 'itero',
    patient: 'iteroPatient',
    chart: 'iteroChart',
    order: 'iteroOrder',
    orderTab: 'iteroOrderTab'
} as const;

const ITERO_PARAM_NAMES: readonly string[] = Object.values(ITERO_PARAMS);
const SCANS_TAB: OrderTabId = 'scans';
const CLOSED_VIEW = 'closed';

// A blank chart number is kept so an in-session click works; it just can't be restored by search.
export interface IteroPatientRef {
    id: string;
    chartNumber: string;
}

export interface IteroOrderRef {
    rxId: string;
    tab: OrderTabId;
}

// On Orders the patient is always null; on Patients an order is the scan drilled into from it.
export interface IteroSelection {
    patient: IteroPatientRef | null;
    order: IteroOrderRef | null;
}

export interface IteroBrowserRoute {
    tab: IteroBrowserTab;
    selection: IteroSelection;
}

export const NO_SELECTION: IteroSelection = { patient: null, order: null };

const hasText = (value: string | null): value is string => value !== null && value.trim() !== '';

function readTab(value: string | null): IteroBrowserTab | null {
    return value === 'patients' || value === 'orders' ? value : null;
}

function readOrder(params: URLSearchParams): IteroOrderRef | null {
    const rxId = params.get(ITERO_PARAMS.order);
    if (!hasText(rxId)) return null;
    const tab: OrderTabId = params.get(ITERO_PARAMS.orderTab) === SCANS_TAB ? 'scans' : 'details';
    return { rxId, tab };
}

function readPatientsSelection(params: URLSearchParams): IteroSelection {
    const id = params.get(ITERO_PARAMS.patient);
    if (!hasText(id)) return NO_SELECTION;
    const chart = params.get(ITERO_PARAMS.chart);
    const patient = { id, chartNumber: hasText(chart) ? chart : '' };
    return { patient, order: readOrder(params) };
}

export function parseIteroBrowserRoute(params: URLSearchParams): IteroBrowserRoute | null {
    const tab = readTab(params.get(ITERO_PARAMS.tab));
    if (tab === null) return null;
    if (tab === 'patients') return { tab, selection: readPatientsSelection(params) };
    return { tab, selection: { patient: null, order: readOrder(params) } };
}

// Every other param (the page's tab and section included) is kept as it was.
export function writeIteroBrowserRoute(
    prev: URLSearchParams,
    route: IteroBrowserRoute | null
): URLSearchParams {
    const next = new URLSearchParams(prev);
    ITERO_PARAM_NAMES.forEach((name) => next.delete(name));
    if (route === null) return next;
    next.set(ITERO_PARAMS.tab, route.tab);
    const { patient, order } = route.selection;
    if (route.tab === 'patients' && patient) {
        next.set(ITERO_PARAMS.patient, patient.id);
        if (hasText(patient.chartNumber)) next.set(ITERO_PARAMS.chart, patient.chartNumber);
    }
    if (order && (route.tab === 'orders' || patient)) {
        next.set(ITERO_PARAMS.order, order.rxId);
        if (order.tab === SCANS_TAB) next.set(ITERO_PARAMS.orderTab, SCANS_TAB);
    }
    return next;
}

function iteroEntries(params: URLSearchParams): string {
    return [...params.entries()]
        .filter(([name]) => ITERO_PARAM_NAMES.includes(name))
        .map(([name, value]) => `${name}=${value}`)
        .sort()
        .join('&');
}

export function hasIteroParams(params: URLSearchParams): boolean {
    return iteroEntries(params) !== '';
}

// False when the URL carries an unknown value, a stray param or a duplicate the parse dropped.
export function isCanonicalIteroRoute(params: URLSearchParams): boolean {
    const route = parseIteroBrowserRoute(params);
    return iteroEntries(params) === iteroEntries(writeIteroBrowserRoute(params, route));
}

export function iteroRouteView(route: IteroBrowserRoute | null): string {
    if (route === null) return CLOSED_VIEW;
    const { patient, order } = route.selection;
    return [route.tab, patient?.id ?? '', order?.rxId ?? ''].join('|');
}

// One level up: a drilled scan goes back to its patient, anything else to the bare table.
export function parentSelection(selection: IteroSelection): IteroSelection {
    if (selection.patient && selection.order) return { patient: selection.patient, order: null };
    return NO_SELECTION;
}

export function sameSelection(a: IteroSelection, b: IteroSelection): boolean {
    return (
        a.patient?.id === b.patient?.id &&
        a.patient?.chartNumber === b.patient?.chartNumber &&
        a.order?.rxId === b.order?.rxId &&
        a.order?.tab === b.order?.tab
    );
}
